import { useState } from 'react';
import { Button, Card, Form, Input, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';

import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

interface ChangePasswordFormValues {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export default function ChangePassword() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const mustChange = useAuthStore((s) => s.user?.must_change_password);

  const onFinish = async (values: ChangePasswordFormValues) => {
    setLoading(true);
    try {
      // API payload key 锁 backend: old_password + new_password（UI label ≠ API key）
      await api.post('/auth/change-password', {
        old_password: values.current_password,
        new_password: values.new_password,
      });
      message.success('密码修改成功，请重新登录');
      clearAuth(); // backend revoke 全 session,frontend 清 store
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        message.error('当前密码错误');
      } else if (status === 400) {
        message.error('新密码不符合强度要求（至少 12 位）');
      } else {
        message.error('修改失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5',
        padding: 16,
      }}
    >
      <Card title="修改密码" style={{ width: 'min(100% - 32px, 480px)', maxWidth: 480 }}>
        {mustChange && (
          <Typography.Paragraph type="warning">
            首次登录需要修改密码后才能使用系统。
          </Typography.Paragraph>
        )}
        <Form layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="当前密码"
            name="current_password"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="new_password"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 12, message: '密码至少 12 位' },
            ]}
            extra="至少 12 位，建议含字母 + 数字"
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirm_password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              提交
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
